import React, { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { HOME, WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import {
	GO_HOME_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { EditorForYouSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorForYouSpotlight.ts';

import { KeyboardShortcutWrapper } from '../../GlobalNavigation/KeyboardShortcutWrapper.tsx';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(
			await import(
				/* webpackChunkName: "loadable-analyticsCallbacks" */ '../../GlobalNavigation/analyticsCallbacks.ts'
			)
		).fireHomeClickedAnalytics,
);

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};

export const useForYouConfig = () => {
	//takes globalItemProps but is not customizable so doesn't use them
	const routeDataRef = useRouteDataRef();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const isSelected = useRouteUrl(isOnHomeSelector);

	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const homeUrl = HOME.toUrl();

	const ForYouItemWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => (
			<KeyboardShortcutWrapper
				href={homeUrl}
				deprecatedKeyboardShortcut={
					!(isLiveEditMode || isOnEditRoute) ? GO_HOME_SHORTCUT : undefined
				}
				keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
				isAppNavigationShortcut
				onShortcutTriggered={fireClickAnalytics}
			>
				<HomeOnboardingTakeoverV2HomeNudge>
					<EditorForYouSpotlight>{children}</EditorForYouSpotlight>
				</HomeOnboardingTakeoverV2HomeNudge>
			</KeyboardShortcutWrapper>
		),
		[homeUrl, isLiveEditMode, isOnEditRoute, fireClickAnalytics],
	);

	return useMemo(
		() => ({
			forYouItem: {
				url: homeUrl,
				isSelected,
				onClick: fireClickAnalytics,
				ItemWrapper: ForYouItemWrapper,
			},
		}),
		[homeUrl, isSelected, fireClickAnalytics, ForYouItemWrapper],
	);
};
